import { environment } from '../config/environment';
import { getApiHeaders, getApiHeadersForGet } from '../utils/apiUtils';

const API_BASE_URL = environment.apiBaseUrl;

class ParticipantService {
  // Contar participantes por concurso
  async getParticipantCountByContest(contestId) {
    try {
      const response = await fetch(`${API_BASE_URL}/participants/count/${contestId}`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error counting participants');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error counting participants:', error);
      throw error;
    }
  }

  /** Bulk participant counts — one request for all contests on the dashboard. */
  async getParticipantCountsByContestIds(contestIds = []) {
    const uniqueIds = [...new Set(contestIds.filter(Boolean))];
    if (uniqueIds.length === 0) {
      return {};
    }

    try {
      const url = `${API_BASE_URL}/participants/counts?contestIds=${encodeURIComponent(uniqueIds.join(','))}`;
      const response = await fetch(url, { headers: getApiHeadersForGet() });

      if (!response.ok) {
        throw new Error('Error counting participants in bulk');
      }

      const data = await response.json();
      return data?.counts || {};
    } catch (error) {
      console.error('Error counting participants in bulk:', error);
      throw error;
    }
  }

  // Simular un participante
  async simulateParticipant(contestId, entranceFee = null, userEmail = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/participants/simulate`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify({
          contestId,
          entranceFee,
          userEmail
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error simulating participant');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error simulating participant:', error);
      throw error;
    }
  }

  // Obtener todos los participantes
  async getAllParticipants() {
    try {
      const response = await fetch(`${API_BASE_URL}/participants`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error fetching participants');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching participants:', error);
      throw error;
    }
  }

  // Obtener participantes por concurso
  async getParticipantsByContest(contestId) {
    try {
      const response = await fetch(`${API_BASE_URL}/participants/contest/${contestId}`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error fetching participants by contest');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching participants by contest:', error);
      throw error;
    }
  }

  // Obtener un participante por ID
  async getParticipantById(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/participants/${id}`, {
        headers: getApiHeadersForGet()
      });
      
      if (!response.ok) {
        throw new Error('Error fetching participant');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching participant:', error);
      throw error;
    }
  }

  // Crear un participante
  async createParticipant(participantData) {
    try {
      const response = await fetch(`${API_BASE_URL}/participants`, {
        method: 'POST',
        headers: getApiHeaders(),
        body: JSON.stringify(participantData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error creating participant');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating participant:', error);
      throw error;
    }
  }

  // Actualizar un participante
  async updateParticipant(id, participantData) {
    try {
      const response = await fetch(`${API_BASE_URL}/participants/${id}`, {
        method: 'PUT',
        headers: getApiHeaders(),
        body: JSON.stringify(participantData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error updating participant');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating participant:', error);
      throw error;
    }
  }

  // Eliminar un participante
  async deleteParticipant(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/participants/${id}`, {
        method: 'DELETE',
        headers: getApiHeadersForGet()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error deleting participant');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting participant:', error);
      throw error;
    }
  }
}

export default new ParticipantService();
